<template>
  <div class="wrap">
    <div class="card">
      <div class="brand">
        <img class="brandLogo" :src="ravenLogo" alt="Raven" />
        <div class="h">Raven GTM</div>
      </div>
      
      <template v-if="authMode === 'external'">
        <div class="sub">External Authentication</div>
        <div class="external-message">
          <p>Please authenticate via the main application to access Raven GTM.</p>
        </div>
        <button class="btn primary external-btn" @click="redirectToExternalLogin">
          Go to Login
        </button>
      </template>

      <template v-else>
        <div class="sub">{{ mode === 'login' ? 'Sign in to continue' : 'Enter workspace details' }}</div>

        <div v-if="mode === 'register'" class="field">
          <label>Workspace name</label>
          <input class="input" v-model="workspaceName" placeholder="Acme" />
        </div>

        <div v-if="mode === 'register'" class="field">
          <label>First name</label>
          <input class="input" v-model="firstName" placeholder="Jeff" />
        </div>

        <div v-if="mode === 'register'" class="field">
          <label>Last name</label>
          <input class="input" v-model="lastName" placeholder="Falk" />
        </div>

        <div class="field">
          <label>Email</label>
          <input class="input" v-model="email" placeholder="you@company.com" />
        </div>

        <div class="field">
          <label>Password</label>
          <input class="input" type="password" v-model="password" placeholder="••••••••" />
        </div>

        <div v-if="error" class="error">{{ error }}</div>

        <button class="btn primary" :disabled="loading" @click="submit">
          {{ loading ? 'Working…' : (mode === 'login' ? 'Login' : 'Create workspace') }}
        </button>

        <button class="linkBtn" type="button" @click="mode = (mode === 'login' ? 'register' : 'login')">
          {{ mode === 'login' ? 'Create a new workspace' : 'Login to an existing workspace' }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import {ref} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {apiFetch} from '../services/api.ts'
import {buildExternalLoginUrl, getAuthMode, setAccessToken} from '../services/auth'
import ravenLogo from '../assets/raven.svg'

const router = useRouter()
const route = useRoute()

const authMode = getAuthMode()

const mode = ref('login')
const workspaceName = ref('')
const firstName = ref('')
const lastName = ref('')
const email = ref('')
const password = ref('')

const error = ref('')
const loading = ref(false)

function redirectToExternalLogin() {
  const returnPath = route.query.returnPath || route.fullPath || '/home'
  window.location.assign(buildExternalLoginUrl(returnPath))
}

async function submit() {
  error.value = ''
  loading.value = true
  try {
    let payload
    let path
    if (mode.value === 'login') {
      path = '/auth/login'
      payload = { email: email.value, password: password.value }
    } else {
      path = '/auth/register'
      payload = {
        workspace_name: workspaceName.value,
        first_name: firstName.value,
        last_name: lastName.value,
        email: email.value,
        password: password.value,
      }
    }

    const resp = await apiFetch(path, { method: 'POST', body: payload })
    setAccessToken(resp.access_token)
    router.push('/home')
  } catch (e) {
    error.value = e?.message || 'Request failed'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.wrap {
  height: 100vh;
  display: grid;
  place-items: center;
  background: #f9fafb;
}

.card {
  width: min(420px, 92vw);
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.h {
  font-weight: 700;
  font-size: 18px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brandLogo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  display: block;
}

.sub {
  color: var(--muted);
  font-size: 13px;
  margin-top: -8px;
}

.row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

label {
  font-size: 12px;
  color: #374151;
}

.error {
  color: var(--danger);
  font-size: 13px;
}

.external-message {
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  margin: 10px 0;
}

.external-btn {
  text-align: center;
  text-decoration: none;
  display: block;
}

.linkBtn {
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
}

.linkBtn:hover {
  color: #111827;
  text-decoration: underline;
}
</style>
